import React, { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useOutletContext } from 'react-router-dom';
import compactSelectStyles from '../../shared/compactSelectStyles';
import SPACheckbox from '../../shared/SPACheckbox';
import SPASectionHeading from '../../shared/SPASectionHeading';
import SPASelect from '../../shared/SPASelect';
import { useSPAToast } from '../../shared/SPAToast';
import { Field, PanelFooter, panelClass } from '../../shared/formPanel';
import { EditableProgram, ProgramUpdatePayload, useUpdateProgram } from './useProgram';

interface Option {
  label: string;
  value: string;
}

// The left nav's correlations tab picks its standards from this, unless the subscriber's state
// benchmarks take over. The legacy form's "show correlations" checkbox had no reader since 2019
// and is gone.
const CORRELATIONS_TYPE_OPTIONS: Option[] = [
  { label: 'State and Common Core Standards (source: EdGate)', value: 'common_core' },
  { label: 'Next Generation Science Standards (source: TCI)', value: 'ngss' },
];

interface FormValues {
  correlations_type: string;
  show_complete_correlations: boolean;
  show_progressions: boolean;
}

const toFormValues = (program: EditableProgram): FormValues => ({
  correlations_type: program.correlations_type ?? 'common_core',
  show_complete_correlations: program.show_complete_correlations,
  show_progressions: program.show_progressions,
});

// Complete correlations only exist for science programs, so only they send the field.
const toPayload = (
  { show_complete_correlations: complete, ...rest }: FormValues,
  science: boolean,
): ProgramUpdatePayload => (science ? { ...rest, show_complete_correlations: complete } : rest);

const CorrelationsPanel = () => {
  const program = useOutletContext<EditableProgram>();
  const spaToast = useSPAToast();
  const updateProgram = useUpdateProgram(program.id);
  const values = useMemo(() => toFormValues(program), [program]);
  const { control, handleSubmit, register } = useForm<FormValues>({ values });

  const save = (formValues: FormValues) => {
    updateProgram.mutate(toPayload(formValues, program.science), {
      onSuccess: () => spaToast.success('Saved correlations settings.'),
    });
  };

  return (
    <form noValidate className={panelClass} onSubmit={handleSubmit(save)}>
      <SPASectionHeading className="tw-mb-5" title="Correlations" />

      <div className="tw-flex tw-flex-col tw-gap-4">
        <Field className="tw-max-w-md" htmlFor="program_correlations_type" label="Standards">
          <Controller
            control={control}
            name="correlations_type"
            render={({ field }) => (
              <SPASelect<Option, false>
                inputId="program_correlations_type"
                options={CORRELATIONS_TYPE_OPTIONS}
                styles={compactSelectStyles}
                value={CORRELATIONS_TYPE_OPTIONS.find(option => option.value === field.value) ?? null}
                onChange={option => field.onChange(option?.value)}
              />
            )}
          />
        </Field>

        <div className="tw-flex tw-flex-col tw-gap-3">
          <span className="tw-text-sm tw-font-bold tw-text-spa-heading">Lesson correlations popup</span>
          <SPACheckbox {...register('show_progressions')}>Show prior 3D learning</SPACheckbox>
          {program.science && (
            <SPACheckbox {...register('show_complete_correlations')}>Show complete correlations</SPACheckbox>
          )}
        </div>
      </div>

      <PanelFooter update={updateProgram} />
    </form>
  );
};

export default CorrelationsPanel;
